import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router';
import { useSortPreference } from 'hooks/useSortPreference';
import { useSelector } from 'react-redux';
import { Skeleton } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { RootState } from 'store';
import { useFetchPublicWantListQuery } from 'store/services/collection/publicCollectionApi';
import { useCalculatePriceMutation } from 'store/services/trade/tradePriceApi';
import { CollectionList } from 'types/interfaces/collectionList';
import { MTGCard } from 'types/interfaces/mtgCard';
import { buildCardUrl } from 'utils/cardUrlUtils';
import { SortOrder } from 'types/interfaces/sort';
import { Points } from 'components/Points/Points';
import MagicButton from 'components/MagicButton/MagicButton';
import LoginPromptModal from 'components/LoginPromptModal';
import { FoilCardImage } from 'components/FoilCardImage';
import TextInputField from 'components/TextInputField';
import CardsTable from 'components/CardsTable';
import { CardsTableCellsData } from 'components/CardsTable/types';
import { SetIcon } from 'components/SetIcon';
import CardSizeSelector from 'components/CardSizeSelector/CardSizeSelector';
import { AssetUrls } from 'config/assets';
import { makeCardImageErrorHandler } from 'utils/cardImageFallback';
import { useProfileTradeSidebar, SidebarTradeCard } from '../../context/ProfileTradeSidebarContext';
import { SetDetail } from 'store/services/trade/tradeApi';
import { getFinishAvailability } from 'utils/foilAvailability';
import {
  getCardImageUrl,
  getDefaultStagedLanguage,
  getValidCondition,
  wantArtFinish,
  SORT_OPTIONS,
  VIEW_OPTIONS,
  formatConditions,
  formatFoilPreference,
} from 'utils/wantListHelpers';
import { formatLanguages } from 'constants/languages';
import WantListCardOverlay from './WantListCardOverlay';
import WantListCardTooltip from './WantListCardTooltip';
import './ProfileWantListTab.css';
import { useIsMobile } from 'hooks/useIsMobile';
import { useCardFilterState } from 'components/FilterPanel/cardFilterUrlState';
import CardFilterRail from 'components/FilterPanel/CardFilterRail';
import CardFilterTokenRow from 'components/FilterPanel/CardFilterTokenRow';
import CardFilterMobile from 'components/FilterPanel/CardFilterMobile';
import FilteredEmptyState from 'components/FilterPanel/FilteredEmptyState';
import { useGroupedRows } from 'components/FilterPanel/useGroupedRows';
import GroupHeaderRow from 'components/FilterPanel/GroupHeaderRow';
import type { CardsTableBodyRow } from 'components/CardsTable/types';
import { defaultStagedFinish, finishFields, getAvailableFinishes, stagedFinishSource } from 'utils/finishes';

const PROFILE_FILTER_TOOLTIP = {
  heading: "Filter this trader's list",
  body: "Narrows every card shown below. Cards already in your trade are unaffected. Color filters match a card's printed colors; the Color sort groups by color identity.",
};

interface ProfileWantListTabProps {
  username: string;
  isOwnProfile?: boolean;
  receiverPoints?: number;
  /**
   * Phone width with a trade in progress: the cart bar owns the bottom edge and
   * carries the Filters button, so the page controls the sheet's open state.
   */
  mobileFilterSheet?: { open: boolean; onOpenChange: (open: boolean) => void };
}

// Format number with commas
const formatNumber = (num: number): string => {
  return new Intl.NumberFormat('en-US').format(num);
};

// Chevron SVG used in dropdowns
const ChevronSvg: React.FC<{ open: boolean }> = ({ open }) => (
  <svg
    className={`profile-want-dropdown-chevron ${open ? 'open' : ''}`}
    width="12"
    height="12"
    viewBox="0 0 12 12"
    fill="none"
    aria-hidden="true"
  >
    <path d="M3 4.5L6 7.5L9 4.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// Card item component with overlay
interface WantListCardItemProps {
  item: CollectionList;
  isOwnProfile: boolean;
  onAddToTrade: (item: CollectionList) => void;
  addedCount: number;
  showOverlayOnHover: boolean;
  calculatedPrice?: number | null; // Price from calculate-price API
  username: string; // For tooltip display
  onTooltipShow: (item: CollectionList, position: { x: number; y: number }) => void;
  onTooltipHide: () => void;
  fallbackStepRef: React.MutableRefObject<Map<string | number, number>>;
}

const WantListCardItem: React.FC<WantListCardItemProps> = ({
  item,
  isOwnProfile,
  onAddToTrade,
  addedCount,
  showOverlayOnHover,
  calculatedPrice,
  username,
  onTooltipShow,
  onTooltipHide,
  fallbackStepRef,
}) => {
  const navigate = useNavigate();
  const [isHovered, setIsHovered] = useState(false);
  const [isMobileTapped, setIsMobileTapped] = useState(false);
  const imageUrl = getCardImageUrl(item);
  const cardInputs = {
    scryfallId: item.card?.scryfallID,
    image: imageUrl,
  };

  // Affordable Only swap reuses the same component instance (keyed by item.id),
  // so the per-item fallback step counter would persist across a printing swap.
  // Reset it on URL change so the new printing starts from `normal`.
  useEffect(() => {
    fallbackStepRef.current.delete(item.id);
  }, [imageUrl, fallbackStepRef, item.id]);

  const cardName = item.card?.name || item.cardName || 'Unknown Card';

  const handleClick = (e: React.MouseEvent) => {
    // If overlay is showing, don't navigate on card click - use the arrow button instead
    if (showOverlayOnHover) {
      // On mobile, first tap shows overlay
      if (window.innerWidth <= 768 && !isMobileTapped) {
        e.preventDefault();
        e.stopPropagation();
        setIsMobileTapped(true);
        return;
      }
      // When overlay is shown, clicking anywhere on the card does nothing (use arrow to navigate)
      return;
    }

    // Only navigate directly if not showing overlay (e.g., own profile)
    if (item.card) {
      navigate(buildCardUrl(item.card));
    }
  };

  const handleViewCard = () => {
    if (item.card) {
      navigate(buildCardUrl(item.card));
    }
  };

  const handleMouseEnter = (e: React.MouseEvent<HTMLDivElement>) => {
    if (showOverlayOnHover) {
      setIsHovered(true);
      // Wants flyout only makes sense on other users' profiles (self-view omits it)
      if (!isOwnProfile) {
        // Show tooltip - position it to the right of the card
        const rect = e.currentTarget.getBoundingClientRect();
        onTooltipShow(item, { x: rect.right, y: rect.top + 12 });
      }
    }
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
    setIsMobileTapped(false);
    onTooltipHide();
  };

  const handleOverlayClose = () => {
    setIsMobileTapped(false);
    setIsHovered(false);
  };

  const handleAddToTrade = () => {
    onAddToTrade(item);
  };

  const showOverlay = showOverlayOnHover && (isHovered || isMobileTapped);

  return (
    <div
      className="profile-want-card-item"
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className="profile-want-card-image-container">
        <FoilCardImage
          src={imageUrl}
          alt={cardName}
          className="profile-want-card-image"
          finish={wantArtFinish(item)}
          onError={makeCardImageErrorHandler(cardInputs, fallbackStepRef, item.id, 'normal')}
          loading="lazy"
          borderRadius="8px"
        />
        {item.quantity > 1 && !showOverlay && (
          <div className="profile-want-card-quantity">x{item.quantity}</div>
        )}

        {/* Overlay */}
        {showOverlay && (
          <WantListCardOverlay
            cardName={cardName}
            quantity={item.quantity}
            addedCount={addedCount}
            onAddToTrade={handleAddToTrade}
            onViewCard={handleViewCard}
            onClose={isMobileTapped ? handleOverlayClose : undefined}
            isMobile={isMobileTapped}
            mode={isOwnProfile ? 'manage' : 'trade'}
          />
        )}
      </div>
      <div className="profile-want-card-info">
        {(item.displayPrice ?? calculatedPrice) != null && (
          <Points points={(item.displayPrice ?? calculatedPrice)!} type="small" />
        )}
      </div>
    </div>
  );
};

// Loading skeleton
const WantListSkeleton: React.FC = () => (
  <div className="profile-want-skeleton">
    <div className="profile-want-header">
      <Skeleton variant="text" width={120} height={28} sx={{ backgroundColor: 'rgba(255,255,255,0.1)' }} />
    </div>
    <div className="profile-want-grid">
      {[...Array(12)].map((_, i) => (
        <div key={i} className="profile-want-card-item">
          <Skeleton
            variant="rectangular"
            width="100%"
            sx={{
              backgroundColor: 'rgba(255,255,255,0.1)',
              aspectRatio: '488 / 680',
              borderRadius: '8px',
            }}
          />
          <Skeleton variant="text" width="80%" height={16} sx={{ backgroundColor: 'rgba(255,255,255,0.1)', mt: 1 }} />
        </div>
      ))}
    </div>
  </div>
);

export const ProfileWantListTab: React.FC<ProfileWantListTabProps> = ({ username, isOwnProfile = false, receiverPoints, mobileFilterSheet }) => {
  const navigate = useNavigate();
  const currentUser = useSelector((state: RootState) => state.auth.user);
  const { addCard, getAddedCountForWantListId, isOpen: isCartOpen, cards: cartCards } = useProfileTradeSidebar();
  const isPhone = useIsMobile();
  const cardFilters = useCardFilterState();
  const [localSheetOpen, setLocalSheetOpen] = useState(false);
  const sheetOpen = mobileFilterSheet?.open ?? localSheetOpen;
  const setSheetOpen = mobileFilterSheet?.onOpenChange ?? setLocalSheetOpen;

  const [page, setPage] = useState(1);
  const [previousPages, setPreviousPages] = useState<CollectionList[]>([]);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [priceMap, setPriceMap] = useState<Record<number, number>>({});
  const [calculatePrice] = useCalculatePriceMutation();
  const fetchedIdsRef = useRef<Set<number>>(new Set());
  const cardImageFallbackRef = useRef<Map<string | number, number>>(new Map());
  const prevFilterDepsRef = useRef({
    search: '',
    sort: 'created_at' as string | undefined,
    direction: 'desc' as string | undefined,
    affordable: false,
    autoMatch: false,
  });

  // Affordable filter state
  const [showAffordableOnly, setShowAffordableOnly] = useState(false);

  // Auto-match filter state
  const [showAutoMatchOnly, setShowAutoMatchOnly] = useState(false);

  // Search state
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  // Sort state - persisted to URL params and localStorage
  const { orderBy: sortOrder, setOrderBy: setSortOrder } = useSortPreference('profileWantListSort', 'created_at', 'desc');

  // View mode state - persist to sessionStorage
  const [viewMode, setViewMode] = useState<'images' | 'list'>(() => {
    const saved = sessionStorage.getItem('profileWantListViewMode');
    return (saved as 'images' | 'list') || 'images';
  });

  // Image size state - persist to localStorage
  type ImageSize = 'S' | 'M' | 'L';
  const [imageSize, setImageSize] = useState<ImageSize>(() => {
    const saved = localStorage.getItem('profileWantListImageSize');
    return (saved as ImageSize) || 'S';
  });

  const handleImageSizeChange = useCallback((size: ImageSize) => {
    setImageSize(size);
    localStorage.setItem('profileWantListImageSize', size);
  }, []);

  // Dropdown states
  const [isSortOpen, setIsSortOpen] = useState(false);
  const [isViewOpen, setIsViewOpen] = useState(false);

  const sortRef = useRef<HTMLDivElement>(null);
  const viewRef = useRef<HTMLDivElement>(null);

  // Mobile detection
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Debounce search term (300ms)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Synchronous reset during render to prevent stale frame when filters/sort change.
  // Using React's "adjusting state during rendering" pattern instead of useEffect,
  // so previousPages/priceMap are cleared BEFORE the render completes (no stale card data).
  const filterDepsChanged =
    prevFilterDepsRef.current.search !== debouncedSearchTerm ||
    prevFilterDepsRef.current.sort !== sortOrder.sort ||
    prevFilterDepsRef.current.direction !== sortOrder.direction ||
    prevFilterDepsRef.current.affordable !== showAffordableOnly ||
    prevFilterDepsRef.current.autoMatch !== showAutoMatchOnly;

  if (filterDepsChanged) {
    setPage(1);
    setPreviousPages([]);
    setPriceMap({});
    fetchedIdsRef.current.clear();
    prevFilterDepsRef.current = {
      search: debouncedSearchTerm,
      sort: sortOrder.sort,
      direction: sortOrder.direction,
      affordable: showAffordableOnly,
      autoMatch: showAutoMatchOnly,
    };
  }

  // Click outside to close dropdowns
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (sortRef.current && !sortRef.current.contains(event.target as Node)) {
        setIsSortOpen(false);
      }
      if (viewRef.current && !viewRef.current.contains(event.target as Node)) {
        setIsViewOpen(false);
      }
    };
    if (isSortOpen || isViewOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isSortOpen, isViewOpen]);

  // Tooltip state for card hover
  const [tooltipData, setTooltipData] = useState<{
    item: CollectionList;
    position: { x: number; y: number };
  } | null>(null);

  const handleTooltipShow = (item: CollectionList, position: { x: number; y: number }) => {
    setTooltipData({ item, position });
  };

  const handleTooltipHide = () => {
    setTooltipData(null);
  };

  // Sort handlers
  const currentSortValue = `${sortOrder.sort}-${sortOrder.direction}`;
  const currentSortLabel = SORT_OPTIONS.find(o => o.value === currentSortValue)?.label || 'Date Added';

  const handleSortSelect = useCallback((compoundValue: string) => {
    const [sort, direction] = compoundValue.split('-');
    setSortOrder({ sort, direction: direction as SortOrder });
    setIsSortOpen(false);
  }, [setSortOrder]);

  // View mode handler
  const handleViewModeChange = useCallback((mode: 'images' | 'list') => {
    setViewMode(mode);
    sessionStorage.setItem('profileWantListViewMode', mode);
    setIsViewOpen(false);
  }, []);

  // Derive maxPrice from toggle + receiver points
  const maxPrice = showAffordableOnly && receiverPoints != null ? receiverPoints : undefined;

  // Derive autoMatch from toggle + login state
  const autoMatch = showAutoMatchOnly && !!currentUser && !isOwnProfile ? true : undefined;

  const { data, isLoading, isFetching } = useFetchPublicWantListQuery({
    username,
    page,
    limit: 48,
    sort: sortOrder.sort,
    direction: sortOrder.direction,
    search: debouncedSearchTerm || undefined,
    maxPrice,
    autoMatch,
    filters: cardFilters.requestArgs,
  });

  // Filters changed: restart the accumulated pages.
  useEffect(() => {
    setPage(1);
    setPreviousPages([]);
  }, [cardFilters.requestArgs]);

  // Fetch prices when new data loads
  useEffect(() => {
    if (!data?.wantList || data.wantList.length === 0) return;

    // Filter items that need price calculation (no displayPrice and not already fetched)
    const itemsNeedingPrice = data.wantList.filter(
      item => item.displayPrice == null && !fetchedIdsRef.current.has(item.id)
    );

    if (itemsNeedingPrice.length === 0) return;

    // Mark these IDs as fetched immediately to prevent duplicate requests
    itemsNeedingPrice.forEach(item => fetchedIdsRef.current.add(item.id));

    const fetchPrices = async () => {
      try {
        const result = await calculatePrice({
          cards: itemsNeedingPrice.map(item => {
            // Resolve set details to determine physical foil availability
            let setDetails = item.acceptableSetDetails;
            if (!setDetails || setDetails.length === 0) {
              setDetails = item.acceptableSets?.map((setName, idx) => ({
                name: setName,
                code: item.acceptableSetCodes?.[idx] || setName.toLowerCase().replace(/\s+/g, ''),
                cardId: item.acceptedPrintingIds?.[idx] || item.card?.id || 0,
              }));
            }
            const matchingSetDetail = setDetails?.find(
              s => s.cardId === item.card?.id
            ) || setDetails?.[0];

            const defaultFinish = defaultStagedFinish(stagedFinishSource(matchingSetDetail, item.card), item);

            return {
              wantListId: item.id,
              cardId: item.card?.id?.toString(),
              condition: getValidCondition(item.acceptableConditions?.[0]),
              language: 'EN',
              ...finishFields(defaultFinish),
              set: item.card?.setName,
            };
          })
        }).unwrap();

        // Update priceMap with the results
        const newPrices: Record<number, number> = {};
        result.cards.forEach(card => {
          newPrices[card.wantListId] = card.displayPrice;
        });

        setPriceMap(prev => ({ ...prev, ...newPrices }));
      } catch (error) {
        console.error('Failed to calculate prices:', error);
      }
    };

    fetchPrices();
  }, [data?.wantList, calculatePrice]);

  // When loading more pages, accumulate previous results
  const handleLoadMore = () => {
    if (data?.wantList) {
      setPreviousPages((prev) => [...prev, ...data.wantList]);
    }
    setPage((p) => p + 1);
  };

  // Reset when username changes
  useEffect(() => {
    setPage(1);
    setPreviousPages([]);
    setPriceMap({});
    fetchedIdsRef.current.clear();
    setSearchTerm('');
    setDebouncedSearchTerm('');
  }, [username]);

  const handleAddToTrade = useCallback((item: CollectionList) => {
    // If not logged in, show login modal
    if (!currentUser) {
      setShowLoginModal(true);
      return;
    }

    const cardName = item.card?.name || item.cardName || 'Unknown Card';
    const imageUrl = getCardImageUrl(item);

    // Determine default condition (validate against known backend values)
    const defaultCondition = getValidCondition(item.acceptableConditions?.[0]);

    // Resolve set details FIRST so we can derive foil availability from them
    let acceptableSetDetails: SetDetail[] | undefined = item.acceptableSetDetails;

    if (!acceptableSetDetails || acceptableSetDetails.length === 0) {
      acceptableSetDetails = item.acceptableSets?.map((setName, idx) => ({
        name: setName,
        code: item.acceptableSetCodes?.[idx] || setName.toLowerCase().replace(/\s+/g, ''),
        cardId: item.acceptedPrintingIds?.[idx] || item.card?.id || 0,
      }));
    }

    // Find the set detail matching item.card so dropdown aligns with displayed image/price
    const matchingSetDetail = acceptableSetDetails?.find(
      s => s.cardId === item.card?.id
    ) || acceptableSetDetails?.[0];

    // Determine foil availability and default using SetDetail → price data → card.foil fallback
    const acceptFoil = item.acceptFoil ?? true;
    const acceptNonFoil = item.acceptNonFoil ?? true;
    const { availableInFoil, availableInNonFoil } = getFinishAvailability(
      matchingSetDetail, item.card, acceptFoil, acceptNonFoil,
    );
    // Three-value default (Track D): the first finish the printing has and the want accepts
    const finishSource = stagedFinishSource(matchingSetDetail, item.card);
    const defaultFinish = defaultStagedFinish(finishSource, item);

    const newCard: Omit<SidebarTradeCard, 'id'> = {
      wantListId: item.id,
      cardId: matchingSetDetail?.cardId || item.card?.id,
      cardName,
      set: matchingSetDetail?.name || item.card?.setName || 'Unknown Set',
      setCode: matchingSetDetail?.code || item.card?.setCode || '',
      condition: defaultCondition,
      language: getDefaultStagedLanguage(item),
      acceptableLanguages: item.acceptableLanguages,
      ...finishFields(defaultFinish),
      imageUrl,
      pointValue: item.displayPrice ?? priceMap[item.id] ?? 0,
      acceptableSetDetails,
      acceptableConditions: item.acceptableConditions,
      acceptFoil,
      acceptNonFoil,
      acceptEtched: item.acceptEtched,
      availableInFoil,
      availableInNonFoil,
      availableFinishes: getAvailableFinishes(finishSource),
      scryfallId: matchingSetDetail?.scryfallId || item.card?.scryfallID,
      availableLanguages: item.card?.availableLanguages,
      maxQuantity: item.quantity,
    };

    addCard(newCard);
  }, [currentUser, priceMap, addCard]);

  // Combine previous pages with current page data
  const allItems = useMemo(
    () => [...previousPages, ...(data?.wantList || [])],
    [previousPages, data?.wantList]
  );

  // Track debounce pending state to avoid flash of "no results"
  const isSearchPending = searchTerm !== debouncedSearchTerm;

  const totalCards = data?.totalCards || 0;
  const totalValue = data?.totalValue || 0;
  const hasMore = data?.hasMore || false;
  // Both views get the hover overlay; the mode prop differentiates them
  // (self-view → "Manage" link, other profiles → "Add to Trade").
  const showOverlayOnHover = true;

  // Generate table cells for list view
  const tableCellsData: CardsTableCellsData = useMemo(() => {
    if (viewMode !== 'list') return { headCells: [], bodyRows: [] };

    const headCells: { key: string; label: string; columnWidth?: number; noSort?: boolean }[] = [
      { key: 'card_name', label: 'Name' },
      { key: 'setName', label: 'Set', columnWidth: 80, noSort: true },
    ];

    if (!isMobile) {
      headCells.push(
        { key: 'condition', label: 'Cond', columnWidth: 60, noSort: true },
        { key: 'lang', label: 'Lang', columnWidth: 55, noSort: true },
        { key: 'isFoil', label: 'Foil', columnWidth: 50, noSort: true },
        { key: 'reference_price', label: 'Price', columnWidth: 80, noSort: true },
      );
    }

    headCells.push({ key: 'quantity', label: 'Qty', columnWidth: 50, noSort: true });

    if (!isOwnProfile) {
      headCells.push({ key: 'action', label: '', columnWidth: 100, noSort: true });
    }

    return {
      headCells,
      bodyRows: allItems.map((row) => {
        const components: React.ReactNode[] = [
          // Name with thumbnail
          <div className="collection-card-name" key="name">
            {row.card?.scryfallID && (
              <img
                src={AssetUrls.cards.getCardImage(row.card.scryfallID, 'art_crop')}
                className="collection-card-name-img"
                alt={row.cardName || row.card?.name || 'Card'}
                onError={(e) => {
                  const img = e.currentTarget;
                  const scryfallId = row.card?.scryfallID;
                  if (scryfallId && !img.dataset.formatFallbackComplete) {
                    const currentSrc = img.src;
                    if (currentSrc.includes('/art_crop/')) {
                      img.src = AssetUrls.cards.getCardImage(scryfallId, 'normal');
                      return;
                    } else if (currentSrc.includes('/normal/')) {
                      img.src = AssetUrls.cards.getCardImage(scryfallId, 'small');
                      return;
                    } else if (currentSrc.includes('/small/')) {
                      img.src = AssetUrls.cards.getCardImage(scryfallId, 'large');
                      return;
                    }
                    img.dataset.formatFallbackComplete = 'true';
                  }
                  img.src = AssetUrls.cards.defaultBack;
                }}
              />
            )}
            <span className="collection-card-name-text">
              {row.cardName || row.card?.name}
            </span>
          </div>,
          // Set with icon + code
          <div className="collection-card-set" key="set">
            {(!row.acceptableSets || row.acceptableSets.length === 0) ? (
              <span className="collection-card-set-text">Any</span>
            ) : (
              <>
                <SetIcon
                  setIconUrl={
                    row.card?.setIconUrl ||
                    (row.acceptableSetCodes?.[0]
                      ? `${AssetUrls.cardsCDN}/sets/icons/${row.acceptableSetCodes[0]}.svg`
                      : undefined)
                  }
                  setName={row.acceptableSets[0]}
                  inverted={false}
                  size="sm"
                />
                <span className="collection-card-set-code">
                  {(row.acceptableSetCodes?.[0] || row.card?.setCode || '').toUpperCase()}
                </span>
                {row.acceptableSets.length > 1 && (
                  <span className="collection-card-set-more" title={row.acceptableSets.join(', ')}>
                    +{row.acceptableSets.length - 1}
                  </span>
                )}
              </>
            )}
          </div>,
        ];

        if (!isMobile) {
          components.push(
            <div key="cond">{formatConditions(row.acceptableConditions)}</div>,
            <div key="lang">{formatLanguages(row.acceptableLanguages)}</div>,
            <div key="foil">{formatFoilPreference(row)}</div>,
            <div key="price">
              {(row.displayPrice ?? priceMap[row.id]) != null
                ? <Points points={(row.displayPrice ?? priceMap[row.id])!} type="small" />
                : '???'}
            </div>,
          );
        }

        // Compute added state for both Qty and Action cells
        const addedCount = isOwnProfile ? 0 : getAddedCountForWantListId(row.id);
        const remaining = row.quantity - addedCount;
        const allAdded = remaining <= 0;
        const partiallyAdded = addedCount > 0 && !allAdded;

        // Quantity (after price, just before action)
        components.push(
          <div className="collection-card-quantity-container" key="qty">
            {allAdded ? (
              <span className="profile-want-list-qty-done">&#10003;</span>
            ) : partiallyAdded ? (
              <span className="profile-want-list-qty-partial">{remaining} left</span>
            ) : (
              <span>{row.quantity}</span>
            )}
          </div>,
        );

        if (!isOwnProfile) {
          components.push(
            <div key="action">
              {allAdded ? (
                <span className="profile-want-list-added">Added</span>
              ) : (
                <button
                  className="profile-want-list-trade-btn"
                  onClick={(e) => { e.stopPropagation(); handleAddToTrade(row); }}
                >
                  Add to Trade
                </button>
              )}
            </div>
          );
        }

        return {
          key: row.id.toString(),
          card: row.card || ({} as MTGCard),
          components,
          onClick: () => {
            if (row.card) {
              navigate(buildCardUrl(row.card));
            }
          },
        };
      }),
    };
  }, [viewMode, allItems, isMobile, isOwnProfile, priceMap, getAddedCountForWantListId, handleAddToTrade, navigate]);

  const sortCompound = sortOrder.sort ? `${sortOrder.sort}-${sortOrder.direction ?? 'asc'}` : undefined;
  const { sections: groupSections, kind: groupKind, toggle: toggleGroup } = useGroupedRows(
    allItems,
    data?.groups,
    sortCompound,
    'profileWantList',
  );
  const groupedTableCellsData = useMemo(() => {
    if (!groupSections || !groupKind) return tableCellsData;
    const byKey = new Map(tableCellsData.bodyRows.map((r) => [r.key, r]));
    const bodyRows: CardsTableBodyRow[] = [];
    for (const section of groupSections) {
      const header = (
        <GroupHeaderRow
          as="table-row"
          kind={groupKind}
          group={section.group}
          collapsed={section.collapsed}
          onToggle={() => toggleGroup(section.group.key)}
          colSpan={tableCellsData.headCells.length}
        />
      );
      const rows = section.collapsed ? [] : section.rows;
      if (rows.length === 0) {
        bodyRows.push({ key: `group-${section.group.key}`, card: (section.rows[0]?.card ?? {}) as CardsTableBodyRow['card'], components: [], groupHeader: header, className: 'cards-table-group-placeholder' });
        continue;
      }
      rows.forEach((row, i) => {
        const bodyRow = byKey.get(row.id.toString());
        if (bodyRow) bodyRows.push(i === 0 ? { ...bodyRow, groupHeader: header } : bodyRow);
      });
    }
    return { headCells: tableCellsData.headCells, bodyRows };
  }, [tableCellsData, groupSections, groupKind, toggleGroup]);
  const renderGroupedGrid = (renderItems: (rows: CollectionList[]) => React.ReactNode[]): React.ReactNode[] => {
    if (!groupSections || !groupKind) return renderItems(allItems);
    return groupSections.flatMap((section) => [
      <GroupHeaderRow key={`group-${section.group.key}`} as="block" kind={groupKind} group={section.group} collapsed={section.collapsed} onToggle={() => toggleGroup(section.group.key)} />,
      ...(section.collapsed ? [] : renderItems(section.rows)),
    ]);
  };

  const unfilteredTotal = data?.unfilteredTotal ?? 0;
  const showFilteredEmpty =
    cardFilters.hasActiveFilters && allItems.length === 0 && !isFetching && !isSearchPending && unfilteredTotal > 0;
  if (isLoading) {
    return <WantListSkeleton />;
  }

  // Controls bar (rendered in all states except initial loading)
  const renderControls = () => (
    <div className="profile-want-controls">
      <div className="profile-want-search">
        <TextInputField
          leftComponent={<FontAwesomeIcon icon="magnifying-glass" color="#DD5BEA" size="sm" />}
          value={searchTerm}
          placeholder="Card Name"
          onChange={setSearchTerm}
        />
      </div>

      {/* Affordable Only Toggle - only for other users' profiles */}
      {!isOwnProfile && receiverPoints != null && (
        <div className="profile-want-affordable-toggle">
          <label className="profile-want-affordable-label">
            <span className="profile-want-affordable-text">Affordable Only</span>
            <div className="profile-want-toggle-switch">
              <input
                type="checkbox"
                checked={showAffordableOnly}
                onChange={(e) => setShowAffordableOnly(e.target.checked)}
              />
              <span className="profile-want-toggle-slider" />
            </div>
          </label>
        </div>
      )}

      {/* Auto-Match Toggle - only for logged-in users viewing other profiles */}
      {!isOwnProfile && !!currentUser && (
        <div className="profile-want-automatch-toggle">
          <label className="profile-want-automatch-label">
            <span className="profile-want-automatch-icon">&#9889;</span>
            <span className="profile-want-automatch-text">Auto-Match</span>
            <div className="profile-want-toggle-switch">
              <input
                type="checkbox"
                checked={showAutoMatchOnly}
                onChange={(e) => setShowAutoMatchOnly(e.target.checked)}
              />
              <span className="profile-want-toggle-slider" />
            </div>
          </label>
        </div>
      )}

      {/* View mode dropdown (desktop) */}
      <div className="profile-want-view-selector profile-want-desktop-only" ref={viewRef}>
        <span className="profile-want-dropdown-label">View</span>
        <button
          type="button"
          className={`profile-want-dropdown-trigger ${isViewOpen ? 'open' : ''}`}
          onClick={() => setIsViewOpen(!isViewOpen)}
          aria-haspopup="listbox"
          aria-expanded={isViewOpen}
          aria-label="Select view mode"
        >
          <img
            src={VIEW_OPTIONS.find(o => o.value === viewMode)?.icon}
            alt=""
            className="profile-want-dropdown-icon"
          />
          <span className="profile-want-dropdown-text">
            {VIEW_OPTIONS.find(o => o.value === viewMode)?.label}
          </span>
          <ChevronSvg open={isViewOpen} />
        </button>
        {isViewOpen && (
          <div className="profile-want-dropdown-menu" role="listbox">
            {VIEW_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                className={`profile-want-dropdown-option ${viewMode === option.value ? 'selected' : ''}`}
                onClick={() => handleViewModeChange(option.value)}
                role="option"
                aria-selected={viewMode === option.value}
              >
                <img src={option.icon} alt="" className="profile-want-dropdown-option-icon" />
                <span>{option.label}</span>
              </button>
            ))}
            {viewMode === 'images' && (
              <>
                <div className="profile-want-dropdown-divider" />
                <div className="profile-want-size-toggle">
                  <span className="profile-want-size-label">Size</span>
                  <CardSizeSelector value={imageSize} onChange={handleImageSizeChange} stopPropagation />
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Sort dropdown (desktop) */}
      <div className="profile-want-sort-selector profile-want-desktop-only" ref={sortRef}>
        <span className="profile-want-dropdown-label">Sort</span>
        <button
          type="button"
          className={`profile-want-dropdown-trigger ${isSortOpen ? 'open' : ''}`}
          onClick={() => setIsSortOpen(!isSortOpen)}
          aria-haspopup="listbox"
          aria-expanded={isSortOpen}
          aria-label="Select sort order"
        >
          <img src={AssetUrls.icons.controls.sort} alt="" className="profile-want-dropdown-icon" />
          <span className="profile-want-dropdown-text">{currentSortLabel}</span>
          <ChevronSvg open={isSortOpen} />
        </button>
        {isSortOpen && (
          <div className="profile-want-dropdown-menu" role="listbox">
            {SORT_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                className={`profile-want-dropdown-option ${currentSortValue === option.value ? 'selected' : ''}`}
                onClick={() => handleSortSelect(option.value)}
                role="option"
                aria-selected={currentSortValue === option.value}
              >
                <span>{option.label}</span>
              </button>
            ))}
          </div>
        )}
      </div>

    </div>
  );

  // Determine empty state
  const isEmpty = allItems.length === 0;
  const showSearchEmptyState = isEmpty && !showFilteredEmpty && debouncedSearchTerm && !isSearchPending && !isFetching;
  const showAutoMatchEmptyState = isEmpty && !showFilteredEmpty && showAutoMatchOnly && !debouncedSearchTerm && !isSearchPending && !isFetching;
  const showGenericEmptyState = isEmpty && !showFilteredEmpty && !debouncedSearchTerm && !isSearchPending && !isFetching && !showAutoMatchOnly;
  const showLoadingState = isEmpty && (isSearchPending || isFetching);

  return (
    <>
      <div className="profile-want-tab">
        <div className="profile-want-header">
          <span className="profile-want-count">
            {(showAffordableOnly || showAutoMatchOnly) && data?.unfilteredTotalCards != null
              ? `Showing ${formatNumber(totalCards)} of ${formatNumber(data.unfilteredTotalCards)} cards`
              : `${formatNumber(totalCards)} Cards`
            }
          </span>
          {totalValue > 0 && <Points points={totalValue} />}
        </div>

        {renderControls()}

        <CardFilterTokenRow
          state={cardFilters.state}
          tokens={cardFilters.tokens}
          onChange={cardFilters.update}
          onClearToken={cardFilters.clearToken}
          onClearAll={cardFilters.clearAll}
          scrollable={isPhone}
        />

        {showFilteredEmpty && (
          <FilteredEmptyState
            listNoun="this trader's want list"
            unfilteredTotal={unfilteredTotal}
            state={cardFilters.state}
            tokens={cardFilters.tokens}
            lastAddedToken={cardFilters.lastAddedToken}
            onClearToken={cardFilters.clearToken}
            onClearAll={cardFilters.clearAll}
          />
        )}

        {showLoadingState && (
          <div className={`profile-want-grid size-${imageSize.toLowerCase()}`}>
            {[...Array(8)].map((_, i) => (
              <div key={i} className="profile-want-card-item">
                <Skeleton
                  variant="rectangular"
                  width="100%"
                  sx={{ backgroundColor: 'rgba(255,255,255,0.1)', aspectRatio: '488 / 680', borderRadius: '8px' }}
                />
              </div>
            ))}
          </div>
        )}

        {showSearchEmptyState && (
          <div className="profile-want-no-results">
            <p>No cards matching "{debouncedSearchTerm}"</p>
          </div>
        )}

        {showAutoMatchEmptyState && (
          <div className="profile-want-no-results">
            <p>No cards from your Have List match this user's wants.</p>
          </div>
        )}

        {showGenericEmptyState && (
          <div className="profile-want-empty">
            <p>This user's want list is empty.</p>
          </div>
        )}

        {!isEmpty && viewMode === 'images' && (
          <div className={`profile-want-grid size-${imageSize.toLowerCase()}`}>
            {renderGroupedGrid((rows) => rows.map((item) => (
              <WantListCardItem
                key={item.id}
                item={item}
                isOwnProfile={isOwnProfile}
                onAddToTrade={handleAddToTrade}
                addedCount={getAddedCountForWantListId(item.id)}
                showOverlayOnHover={showOverlayOnHover}
                calculatedPrice={priceMap[item.id]}
                username={username}
                onTooltipShow={handleTooltipShow}
                onTooltipHide={handleTooltipHide}
                fallbackStepRef={cardImageFallbackRef}
              />
            )))}
          </div>
        )}

        {!isEmpty && viewMode === 'list' && (
          <div className="profile-want-list-view">
            <CardsTable
              isLoading={false}
              cellsData={groupedTableCellsData}
              orderBy={sortOrder.sort}
              order={sortOrder.direction}
            />
          </div>
        )}

        {hasMore && !isEmpty && (
          <div className="profile-want-load-more">
            <MagicButton
              label={isFetching ? 'Loading...' : 'Load More'}
              color="dark-grey"
              onPress={handleLoadMore}
              isLoading={isFetching}
              isDisabled={isFetching}
            />
          </div>
        )}
      </div>

      {!isPhone && (
        <CardFilterRail filters={cardFilters} headerTooltip={PROFILE_FILTER_TOOLTIP} cartOpen={isCartOpen} hideTradeSection />
      )}
      {isPhone && (
        <CardFilterMobile
          filters={cardFilters}
          totalCount={data?.totalRecords}
          showDock={cartCards.length === 0}
          sheetOpen={sheetOpen}
          onSheetOpenChange={setSheetOpen}
          hideTradeSection
        />
      )}

      {/* Login Prompt Modal */}
      <LoginPromptModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        collectionOwnerUsername={username}
      />

      {/* Card hover tooltip */}
      {tooltipData && !isOwnProfile && (
        <WantListCardTooltip
          cardData={tooltipData.item}
          position={tooltipData.position}
          username={username}
          onClose={handleTooltipHide}
        />
      )}
    </>
  );
};

export default ProfileWantListTab;
